class InlineSearch extends HTMLElement {
    static ELEMENT_NAME = 'inline-search'

    static SELECTORS = {
        formWrapper: '[data-inline-search-form-wrapper]',
        results: '[data-inline-search-results]',
        allResultsLink: '[data-inline-search-all-results-link]',
        closeButton: '[data-inline-search-close-button]'
    }

    static CLASSES = {
        root: InlineSearch.ELEMENT_NAME,
        formWrapper: `${InlineSearch.ELEMENT_NAME}__form-wrapper`,
        results: `${InlineSearch.ELEMENT_NAME}__results`,
        allResultsLink: `${InlineSearch.ELEMENT_NAME}__all-results-link`,
        closeButton: `${InlineSearch.ELEMENT_NAME}__close-button`,
        column: `${InlineSearch.ELEMENT_NAME}__column`,
        columnHeading: `${InlineSearch.ELEMENT_NAME}__column-heading`,
    }

    $elements = {
        form: null,
        formWrapper: null,
        searchInput: null,
        results: null,
        allResultsLink: null,
        closeButton: null,
        productResultsColumn: null,
        queryResultsColumn: null,
    }

    ids = {
        results: 'search-results',
        // this must match the id of the section used to render the predictive search results, living in `sections/`.
        predictiveSearchResults: 'predictive-search-results',
        inlineSearchResults: 'inline-search-results'
    }

    searchTerm = ''
    isShowingResults = false
    isShowingProductResults = false
    isFetchingResults = false

    constructor() {
        super()

        this.init()

        this.$elements.searchInput.addEventListener('input', this.debounce(this.handleSearchInputChange))

        // prevent submitting the form
        //this.$elements.form.addEventListener('submit', e => e.preventDefault())

        // also clear the search, when the drawer is closed
        this.$elements.closeButton.addEventListener('click', this.handleCloseButtonClick)

    }

    init() {
        // store elements
        this.$elements.formWrapper = this.querySelector(InlineSearch.SELECTORS.formWrapper)
        this.$elements.form = this.$elements.formWrapper.querySelector('form')
        this.$elements.searchInput = this.$elements.form.querySelector('input[type="search"]')
        this.$elements.results = this.querySelector(InlineSearch.SELECTORS.results)
        this.$elements.allResultsLink = this.querySelector(InlineSearch.SELECTORS.allResultsLink)
        this.$elements.closeButton = this.querySelector(InlineSearch.SELECTORS.closeButton)

        this.$elements.results.setAttribute('id', this.ids.results)

        // add all sorts of attributes for the search input (https://shopify.dev/docs/storefronts/themes/navigation-search/search/predictive-search#the-main-search)
        const searchInputAttributes = {
            'aria-autocomplete': 'list',
            'aria-haspopup': 'true',
            'aria-expanded': 'false',
            'aria-owns': this.ids.results,
            'role': 'combobox',
        }
        Object.entries(searchInputAttributes).forEach(([key, value]) => {
            this.$elements.searchInput.setAttribute(key, value)
        })

        // add classes
        this.classList.add(InlineSearch.CLASSES.root)
        this.$elements.formWrapper.classList.add(InlineSearch.CLASSES.formWrapper)
        this.$elements.results.classList.add(InlineSearch.CLASSES.results)
        this.$elements.allResultsLink.classList.add(InlineSearch.CLASSES.allResultsLink)
        this.$elements.closeButton.classList.add(InlineSearch.CLASSES.closeButton)

        // render the result columns
        this.renderResultColumns()

        this.searchTerm = this.$elements.searchInput.value
    }

    /**
     * FETCH
     */
    async getHTMLSearchResults(term) {
        try {
            const response = await fetch(`/search?q=${encodeURIComponent(term)}&type=product&section_id=${this.ids.inlineSearchResults}`);

            if (response.ok) {
                // all good!
                return response.text()
            } else {
                const error = new Error(response.status);
                this.hideResults()
                throw error;
            }
        } catch(error) {
            console.error('Error fetching search results:', error);
            throw error;
        }
    }

    /**
     * RENDERING
     */
    renderResultColumn(heading) {
        const column = document.createElement('div')
        column.classList.add(InlineSearch.CLASSES.column)
        const columnHeading = document.createElement('h3')
        columnHeading.classList.add(InlineSearch.CLASSES.columnHeading)
        columnHeading.textContent = heading
        column.appendChild(columnHeading)

        return column;
    }

    renderResultColumns() {
        // product results
        const productColumn = this.renderResultColumn('Produkte')
        //const queryColumn = this.renderResultColumn('Vorschläge')
        this.$elements.productResultsColumn = productColumn
        //this.$elements.queryResultsColumn = queryColumn

        //this.$elements.results.appendChild(queryColumn)
        this.$elements.results.appendChild(productColumn)
    }

    /**
     * FUNCTIONS
     */

    showAllResultsLink() {
        this.$elements.allResultsLink.hidden = false
    }

    hideAllResultsLink() {
        // TODO: populate the term with the search term
        this.$elements.allResultsLink.hidden = true
    }

    debounce(func, wait = 300) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    showProductResults(results) {
        if (!this.isShowingResults) {
            this.isShowingProductResults = true
        }
        this.renderProductResults(results)
    }

    hideProductResults() {
        this.clearProductResults();
        this.isShowingProductResults = false
    }

    hideResults() {
        this.$elements.results.hidden = true
        this.hideProductResults()
        this.hideAllResultsLink()
    }

    showResults(results) {
        this.$elements.results.hidden = false
        this.showProductResults(results)
        this.showAllResultsLink()
    }

    renderProductResults(resultsAsText) {
        // it should be noted that the ID of this section is generated by shopify and is not configurable.
        const parsedHTML = new DOMParser()
            .parseFromString(resultsAsText, 'text/html')
            .querySelector(`#shopify-section-${this.ids.inlineSearchResults}`)

        // clear any potential results that have already been rendered
        this.clearProductResults()

        // prepend it to the results
        this.$elements.productResultsColumn.appendChild(parsedHTML)
    }

    clearProductResults() {
        const existingResults = this.$elements.results.querySelector('#predictive-search-results')

        if (existingResults) {
            existingResults.remove()
        }
    }

    populateAllResultsLink(term) {
        const currentLink = new URL(this.$elements.allResultsLink.href)
        const baseLink = currentLink.origin + currentLink.pathname
        const placeholderTerm = this.$elements.allResultsLink.querySelector('[data-placeholder-term]')

        // replace the placeholder term
        placeholderTerm.textContent = term

        // adapt the link
        this.$elements.allResultsLink.href = `${baseLink}?q=${encodeURIComponent(term)}`

    }

    /**
     * EVENTS
     */

    handleCloseButtonClick = (e) => {
        this.$elements.searchInput.value = ''

        this.hideResults()
    }

    handleSearchInputChange = async (e) => {
        this.searchTerm = e.target.value.trim();

        if (!this.searchTerm.length) {
            this.hideResults()
        } else {
            this.isFetchingResults = true

            const [HTMLResultsAsText, JSONResults] = await Promise.all([
                this.getHTMLSearchResults(this.searchTerm),
                //this.getJSONSearchResults(this.searchTerm)
            ])

            this.showResults(HTMLResultsAsText)

            this.populateAllResultsLink(this.searchTerm)
        }
    }
}

customElements.define(InlineSearch.ELEMENT_NAME, InlineSearch);